import React, { useState, useRef, useEffect } from "react";
import { Sparkle, X, MapPin } from "@phosphor-icons/react";
import api from "../lib/api";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

export default function BirthDetailsModal({ astrologerName, onSubmit, onSkip, required = false }) {
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [place, setPlace] = useState("");
  const [timezone, setTimezone] = useState("");
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(false); // true if user picked from dropdown
  const [searchError, setSearchError] = useState(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);
  const wrapperRef = useRef(null);

  const canSubmit = date && hour !== "" && minute !== "" && place && timezone;

  // Close suggestions on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onPlaceChange = (val) => {
    setPlace(val);
    setPicked(false);
    setTimezone(""); // require re-pick to validate timezone
    setSearchError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = val.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setSearching(false);
      // Keep dropdown visible so the "type at least 2 characters" hint shows
      setShowSuggestions(trimmed.length > 0);
      return;
    }
    setSearching(true);
    setShowSuggestions(true);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/places/search`, { params: { q: trimmed } });
        // Ignore stale responses (user kept typing)
        if (myReqId !== reqIdRef.current) return;
        setSuggestions(Array.isArray(data) ? data : []);
        setShowSuggestions(true);
        setSearchError(null);
      } catch (err) {
        if (myReqId !== reqIdRef.current) return;
        console.error("place search failed", err);
        setSuggestions([]);
        setSearchError("Couldn't reach the place search. Check your connection and try again.");
        setShowSuggestions(true);
      } finally {
        if (myReqId === reqIdRef.current) setSearching(false);
      }
    }, 250);
  };

  const pickSuggestion = (s) => {
    setPlace(s.name);
    setTimezone(s.timezone);
    setPicked(true);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const time = `${hour}:${minute}`;
      await onSubmit({ date, time, place, timezone, question });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="birth-details-modal">
      <div className="card-surface rounded-md max-w-lg w-full p-8 relative max-h-[90vh] overflow-y-auto">
        {!required && (
          <button onClick={onSkip} className="absolute top-4 right-4 text-muted hover:text-gold transition-colors" data-testid="birth-skip-btn">
            <X size={18} />
          </button>
        )}
        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Sparkle size={14} weight="fill" /> {required ? "One-time setup" : "Get a tighter reading"}
        </div>
        <h3 className="font-serif-display text-3xl mt-3 font-light">
          {required
            ? "Tell us about your birth"
            : `A few details for ${astrologerName?.split(" ")[0] || "your guide"}`}
        </h3>
        <p className="text-muted text-sm mt-3 font-light">
          {required
            ? "Your kundali is the foundation of every reading on AstroKuber. Share your birth particulars once and every astrologer — AI or human — will have them ready. You'll never be asked again."
            : `Share your birth particulars and ${astrologerName?.split(" ")[0] || "she"} will open your kundali right away — no warm-up questions, straight to the reading.`}
        </p>

        <form onSubmit={handleSubmit} className="mt-7 space-y-4">
          {/* Date */}
          <div>
            <label className="label-eyebrow">Date of birth</label>
            <input
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full mt-2 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm bg-transparent"
              data-testid="birth-date-input"
            />
          </div>

          {/* Time as hour + minute dropdowns */}
          <div>
            <label className="label-eyebrow">Time of birth</label>
            <div className="flex items-center gap-2 mt-2">
              <select
                required
                value={hour}
                onChange={(e) => setHour(e.target.value)}
                className="flex-1 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm bg-bg"
                data-testid="birth-hour-input"
              >
                <option value="">Hour</option>
                {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              <span className="text-muted font-serif-display text-xl">:</span>
              <select
                required
                value={minute}
                onChange={(e) => setMinute(e.target.value)}
                className="flex-1 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm bg-bg"
                data-testid="birth-minute-input"
              >
                <option value="">Minute</option>
                {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <span className="text-muted text-xs ml-1">24-hr</span>
            </div>
          </div>

          {/* Place autocomplete */}
          <div ref={wrapperRef} className="relative">
            <label className="label-eyebrow">Place of birth</label>
            <div className="relative mt-2">
              <input
                type="text"
                required
                placeholder="Start typing a city — e.g. Mumbai, Delhi, London..."
                value={place}
                onChange={(e) => onPlaceChange(e.target.value)}
                onFocus={() => {
                  if (place.trim().length >= 2 && (suggestions.length > 0 || searching)) {
                    setShowSuggestions(true);
                  } else if (place.trim().length > 0 && place.trim().length < 2) {
                    setShowSuggestions(true);
                  }
                }}
                autoComplete="off"
                className="w-full px-3 py-2.5 pr-9 border border-soft rounded-md focus:border-gold outline-none text-sm"
                data-testid="birth-place-input"
              />
              <MapPin size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted" />
            </div>
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg" data-testid="place-suggestions">
                {place.trim().length > 0 && place.trim().length < 2 && (
                  <div className="px-3 py-2.5 text-xs text-muted">Keep typing — at least 2 characters.</div>
                )}
                {searching && (
                  <div className="px-3 py-2.5 text-xs text-muted flex items-center gap-2">
                    <span className="inline-block w-3 h-3 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                    Searching for "{place.trim()}"...
                  </div>
                )}
                {!searching && place.trim().length >= 2 && suggestions.length === 0 && !searchError && (
                  <div className="px-3 py-2.5 text-xs text-muted">
                    No matches for "{place.trim()}". Try a fuller city name (e.g. "Mumbai" instead of "Bombay") or include the country.
                  </div>
                )}
                {searchError && (
                  <div className="px-3 py-2.5 text-xs text-rose-700">{searchError}</div>
                )}
                {suggestions.map((s, i) => (
                  <button
                    type="button"
                    key={`${s.lat}-${s.lon}-${i}`}
                    onClick={() => pickSuggestion(s)}
                    className="w-full text-left px-3 py-2 hover:bg-[#D4AF37]/10 transition-colors border-b border-soft/40 last:border-0"
                    data-testid={`place-option-${i}`}
                  >
                    <div className="text-sm">{s.name}</div>
                    <div className="text-xs text-muted mt-0.5">{s.timezone}</div>
                  </button>
                ))}
              </div>
            )}
            {place && !picked && !showSuggestions && (
              <p className="text-amber-700/80 text-xs mt-1.5">Pick a city from the dropdown to confirm your birthplace.</p>
            )}
            {picked && timezone && (
              <p className="text-muted text-xs mt-1.5">Timezone auto-detected: <span className="text-gold font-mono">{timezone}</span></p>
            )}
          </div>

          {/* Optional question */}
          {!required && (
            <div>
              <label className="label-eyebrow">What's on your mind? <span className="text-muted normal-case tracking-normal">(optional)</span></label>
              <textarea
                rows={2}
                placeholder="e.g. career switch, marriage timing, current Sade-Sati..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="w-full mt-2 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm"
                data-testid="birth-question-input"
              />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            {!required && (
              <button
                type="button"
                onClick={onSkip}
                className="btn-ghost rounded-full px-5 py-2.5 text-sm flex-1"
                data-testid="birth-skip-secondary"
              >
                Skip for now
              </button>
            )}
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className={`btn-primary rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-50 ${required ? "w-full" : "flex-1"}`}
              data-testid="birth-submit-btn"
            >
              {submitting ? "Opening kundali..." : (required ? "Save & continue" : "Begin reading")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
